/**
 * INS-659: shared conditional-logic evaluator for the Lead Form Builder.
 *
 * Lives in `@insurgrid/common` so Parrot's `FormView` (consumer runtime) and
 * Eagle's `PhonePreview` (agent-facing builder preview) evaluate rules the
 * same way. Without this, the preview drifts from real consumer behaviour
 * every time the rule semantics change.
 *
 * The evaluator is shape-agnostic: callers pass a `RuleContext` whose
 * `getSourceValue` resolves a question id to the consumer's effective
 * answer in whatever shape their app uses (Parrot's `PublicQuestion` keys
 * answers by uuid in a few maps; Eagle's preview keys them by the
 * agent-typed mock values). The evaluator only cares about the resolved
 * string / string[] / undefined.
 */
import type {
  LogicCondition,
  QuestionRule,
} from './types/leadForm';

export interface RuleContext {
  /** Resolve the consumer's effective answer for the given question id.
   *
   *  Return values:
   *    - `string` — single answer (text, single-choice label, number-as-text,
   *      ISO date)
   *    - `string[]` — multi-choice selection (option labels)
   *    - `undefined` — unanswered / unknown question id
   */
  getSourceValue(questionId: string): string | string[] | undefined;
  /** Optional: returns false when the source question is currently hidden
   *  by another rule. When supplied, a hidden source behaves as if it had
   *  no answer — prevents stale chains like
   *  `A=1 → show B; B=1 → require C`
   *  from keeping C required after A flips and B disappears. */
  isSourceVisible?(questionId: string): boolean;
}

function isEmpty(v: string | string[] | undefined): boolean {
  if (v === undefined) return true;
  if (Array.isArray(v)) return v.length === 0;
  return v === '';
}

/** Evaluate a single condition against the consumer's current answers. */
export function evaluateCondition(
  cond: LogicCondition,
  ctx: RuleContext,
): boolean {
  const sourceHidden =
    ctx.isSourceVisible !== undefined &&
    !ctx.isSourceVisible(cond.sourceQuestionId);
  const v = sourceHidden ? undefined : ctx.getSourceValue(cond.sourceQuestionId);
  const target = cond.value ?? '';
  switch (cond.operator) {
    case 'isEmpty':
      return isEmpty(v);
    case 'isNotEmpty':
      return !isEmpty(v);
    case 'equals':
      if (Array.isArray(v)) return v.length === 1 && v[0] === target;
      return v === target;
    case 'notEquals':
      if (Array.isArray(v)) return !v.includes(target);
      return v !== target;
    case 'contains':
      if (Array.isArray(v)) return v.includes(target);
      return typeof v === 'string' && v.includes(target);
    case 'notContains':
      if (Array.isArray(v)) return !v.includes(target);
      return typeof v === 'string' && !v.includes(target);
    case 'greaterThan': {
      const n = typeof v === 'string' ? parseFloat(v) : NaN;
      const t = parseFloat(target);
      return Number.isFinite(n) && Number.isFinite(t) && n > t;
    }
    case 'lessThan': {
      const n = typeof v === 'string' ? parseFloat(v) : NaN;
      const t = parseFloat(target);
      return Number.isFinite(n) && Number.isFinite(t) && n < t;
    }
    case 'before':
      return typeof v === 'string' && v !== '' && v < target;
    case 'after':
      return typeof v === 'string' && v !== '' && v > target;
    default:
      return false;
  }
}

/** Evaluate every condition in a rule, combining via the rule's
 *  `conditionMode` (AND for `'all'`, OR for `'any'`). Empty condition
 *  list returns `false` defensively. */
export function evaluateRule(rule: QuestionRule, ctx: RuleContext): boolean {
  if (!rule.conditions?.length) return false;
  if (rule.conditionMode === 'any') {
    return rule.conditions.some((c) => evaluateCondition(c, ctx));
  }
  return rule.conditions.every((c) => evaluateCondition(c, ctx));
}

export interface InitialQuestionState {
  initiallyHidden: boolean;
  required: boolean;
}

/** Rules live on the SOURCE question (the one whose answer triggers them).
 *  To compute a target question's effective visibility we walk every
 *  question in the form and gather rules whose `targetQuestionId` points
 *  at this target. */
export interface QuestionWithRules {
  id: string;
  rules?: QuestionRule[] | null;
}

export function collectRulesAffectingQuestion(
  targetQuestionId: string,
  allQuestions: QuestionWithRules[],
): QuestionRule[] {
  const out: QuestionRule[] = [];
  for (const q of allQuestions) {
    for (const r of q.rules ?? []) {
      if (
        (r.action === 'show' ||
          r.action === 'hide' ||
          r.action === 'require') &&
        r.targetQuestionId === targetQuestionId
      ) {
        out.push(r);
      }
    }
  }
  return out;
}

/** Apply visibility / required rules attached to a single question and
 *  return its effective state. Hide-wins precedence: any matching `hide`
 *  rule overrides all `show` rules. Navigation actions (skip_to_step,
 *  jump_to_question, end_form, continue) are ignored here — they belong
 *  to the step-advance logic, not visibility evaluation. */
export function evaluateRulesForQuestion(
  rules: QuestionRule[] | null | undefined,
  initialState: InitialQuestionState,
  ctx: RuleContext,
): { isVisible: boolean; isRequired: boolean } {
  let isVisible = !initialState.initiallyHidden;
  let isRequired = initialState.required;
  let anyHide = false;

  for (const r of rules ?? []) {
    if (r.action !== 'show' && r.action !== 'hide' && r.action !== 'require') {
      continue;
    }
    if (!evaluateRule(r, ctx)) continue;
    if (r.action === 'show') isVisible = true;
    else if (r.action === 'hide') anyHide = true;
    else if (r.action === 'require') isRequired = true;
  }

  if (anyHide) isVisible = false;
  return { isVisible, isRequired };
}

/**
 * Given a step's question ids IN DISPLAY ORDER and a predicate that reports
 * whether a question's current answer triggers a navigation action
 * (skip_to_step / end_form), return the ids of questions that are unreachable
 * because they sit AFTER the earliest triggered navigation gate on the step.
 * The gate itself is NOT included. Callers remove these from the visible set:
 * a triggered gate skips the remainder of its own step, so those questions must
 * not render and must be exempt from the required-answer check.
 *
 * This helper is action-agnostic: which navigation actions count as a gate is
 * decided entirely by the caller's predicate. Introducing a new nav action
 * means updating each caller's predicate, not this helper.
 */
export function computeStepNavCutoffHiddenIds(
  orderedQuestionIds: string[],
  isNavGateTriggered: (questionId: string) => boolean,
): Set<string> {
  const hidden = new Set<string>();
  let pastGate = false;
  for (const id of orderedQuestionIds) {
    if (pastGate) {
      hidden.add(id);
      continue;
    }
    if (isNavGateTriggered(id)) pastGate = true;
  }
  return hidden;
}
